/**
 * v5Shell.js
 * All non-React JS interactions for Praesidio Care V5.
 * SOURCE: praesidio-care-v5(1).html lines 1943–2165
 *
 * Call initV5Shell() once on app mount (useEffect in App.jsx).
 * Call setupScrollReveal() after every route change.
 * Call animateHero() after route change to home.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

// ─── LOADER ───────────────────────────────────────────────────────────────────
function isIndiaHandoff() {
  const path = window.location.pathname;
  return path === '/entering-in' || path === '/india';
}

function runLoader() {
  if (isIndiaHandoff()) {
    const ld = document.getElementById('loader');
    if (ld) ld.style.display = 'none';
    return;
  }
  const lWord = document.getElementById('lWord');
  if (!lWord || lWord.dataset.init) return;
  lWord.dataset.init = '1';

  'PRAESIDIO'.split('').forEach((ch) => {
    const s = document.createElement('span');
    s.className = 'l-char';
    s.textContent = ch;
    s.style.cssText = 'display:inline-block;transform:translateY(110%);opacity:0';
    lWord.appendChild(s);
  });

  setTimeout(() => {
    const fill = document.getElementById('lBarFill');
    if (fill) fill.style.width = '100%';
  }, 100);

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    const ld = document.getElementById('loader');
    if (ld && ld.style.display !== 'none') ld.style.display = 'none';
    animateHero();
  };

  setTimeout(finish, 3800);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    gsap.set('#lWord .l-char', { y: '0%', opacity: 1 });
    const fill = document.getElementById('lBarFill');
    if (fill) fill.style.width = '100%';
    gsap.delayedCall(0.6, () => {
      gsap.to('#loader', { opacity: 0, duration: 0.35, onComplete: finish });
    });
    return;
  }

  const tl = gsap.timeline();
  tl.to('#lWord .l-char', { y: '0%', opacity: 1, stagger: 0.055, duration: 0.55, ease: 'power3.out', delay: 0.2 })
    .to('#lWord .l-char', { y: '-115%', opacity: 0, stagger: 0.03, duration: 0.4, ease: 'power3.in' }, '+=.7')
    .to('#loader', { clipPath: 'inset(0 0 100% 0)', webkitClipPath: 'inset(0 0 100% 0)', duration: 0.75, ease: 'power4.inOut' })
    .set('#loader', { display: 'none' })
    .add(finish);
}

// ─── HERO ANIMATE IN ──────────────────────────────────────────────────────────
export function animateHero() {
  // Split h1 words, skip React-managed word cycler
  const hTitle = document.getElementById('heroTitle');
  const isWordCycle = hTitle?.dataset?.wordCycle === '1';
  const isStaticHero = hTitle?.dataset?.static === '1' || hTitle?.querySelector('.hwc-accent');
  if (hTitle && !hTitle.dataset.split && !isWordCycle && !isStaticHero) {
    hTitle.dataset.split = '1';
    const txt = hTitle.textContent;
    hTitle.innerHTML = '';
    txt.split(' ').forEach(w => {
      if (!w) return;
      const ws = document.createElement('span');
      ws.className = 'word';
      ws.textContent = w;
      if (w.includes('\u00A0')) ws.style.whiteSpace = 'nowrap'; // e.g. "4\u00A0weeks." stays one line
      if (w === '4' || w === 'weeks.' || (w.includes('4') && w.includes('weeks'))) {
        ws.style.color = 'var(--accent)';
        ws.style.fontStyle = 'italic';
      }
      hTitle.appendChild(ws);
      hTitle.appendChild(document.createTextNode(' '));
    });
  }

  // Force-show all hero elements (failsafe), leave word-cycle h1 alone
  ['.hero-badge', '.hero-tagline', '.hero-p', '.hero-row', '.hero-metrics',
    '.hero-right', '.hero-scroll', '#heroScroll'].forEach(s => {
    document.querySelectorAll(s).forEach(el => {
      el.style.opacity = '1';
      el.style.transform = '';
    });
  });
  if (!isWordCycle) {
    document.querySelectorAll('.hero h1').forEach(el => {
      el.style.opacity = '1';
      el.style.transform = '';
    });
  }
  document.querySelectorAll('.hero-pill').forEach(el => { el.style.opacity = '1'; });

  // GSAP animate in
  try {
    const h2 = gsap.timeline();
    gsap.set('.hero-badge',    { opacity: 0, y: 20 });
    if (!isWordCycle) gsap.set('.hero h1', { opacity: 0, y: 30 });
    gsap.set('.hero-tagline',  { opacity: 0, x: -20 });
    gsap.set('.hero-p',        { opacity: 0, y: 16 });
    gsap.set('.hero-row',      { opacity: 0, y: 16 });
    gsap.set('.hero-metrics',  { opacity: 0, y: 16 });
    gsap.set('.hero-right',    { opacity: 0, y: 32 });
    gsap.set('.hero-pill',     { opacity: 0, y: 10 });
    gsap.set('#heroScroll',    { opacity: 0 });

    h2.to('.hero-badge',   { y: 0, opacity: 1, duration: .5,  ease: 'power3.out' });
    if (!isWordCycle) {
      h2.to('.hero h1', { y: 0, opacity: 1, duration: .65, ease: 'power3.out' }, '-=.2');
    }
    h2.to('.hero-tagline', { x: 0, opacity: 1, duration: .5,  ease: 'power3.out' }, '-=.3')
      .to('.hero-p',       { y: 0, opacity: 1, duration: .5,  ease: 'power3.out' }, '-=.2')
      .to('.hero-row',     { y: 0, opacity: 1, duration: .5,  ease: 'power3.out' }, '-=.15')
      .to('.hero-metrics', { y: 0, opacity: 1, duration: .5,  ease: 'power3.out' }, '-=.1')
      .to('.hero-right',   { y: 0, opacity: 1, duration: .9,  ease: 'power3.out' }, '-=.4')
      .to('.hero-pill',    { y: 0, opacity: 1, stagger: .1, duration: .4, ease: 'power3.out' }, '-=.4')
      .to('#heroScroll',   { opacity: 1, duration: .6 }, '-=.2');
  } catch (e) { /* GSAP not available, CSS fallback already visible */ }
}

// ─── HERO CANVAS (PARTICLE NETWORK) ──────────────────────────────────────────
export function initHeroCanvas() {
  const c = document.getElementById('heroCanvas');
  if (!c || c.dataset.init) return;
  c.dataset.init = '1';

  const ctx = c.getContext('2d');
  let W = 0, H = 0;
  const mouse = { x: null, y: null };
  const pts = [];
  let canvasOnScreen = true;
  let rafId = 0;

  function kick() {
    if (!rafId && canvasOnScreen && !document.hidden && W && H) {
      rafId = requestAnimationFrame(draw);
    }
  }

  function rs() {
    W = c.width = c.offsetWidth;
    H = c.height = c.offsetHeight;
    kick();
  }

  for (let i = 0; i < 75; i++) {
    pts.push({
      x: Math.random() * 3000, y: Math.random() * 2000,
      vx: (Math.random() - .5) * .28, vy: (Math.random() - .5) * .28,
      r: Math.random() * 1.8 + .4
    });
  }

  c.addEventListener('mousemove', e => {
    const r = c.getBoundingClientRect();
    mouse.x = e.clientX - r.left;
    mouse.y = e.clientY - r.top;
  });
  c.addEventListener('mouseleave', () => { mouse.x = null; mouse.y = null; });

  const LINK = 160;
  const LINK2 = LINK * LINK;
  const MOUSE2 = 200 * 200;

  function draw() {
    rafId = 0;
    if (!canvasOnScreen || document.hidden || !W || !H) return;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(200,255,0,.3)';
    ctx.beginPath();
    for (let k = 0; k < pts.length; k++) {
      const p = pts[k];
      p.x += p.vx; p.y += p.vy;
      if (p.x < 0 || p.x > W) p.vx *= -1;
      if (p.y < 0 || p.y > H) p.vy *= -1;
      if (mouse.x !== null) {
        const dx = p.x - mouse.x, dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < MOUSE2 && d2 > 0.01) {
          const d = Math.sqrt(d2);
          p.x += dx / d * 1.5; p.y += dy / d * 1.5;
        }
      }
      ctx.moveTo(p.x + p.r, p.y);
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    }
    ctx.fill();
    ctx.lineWidth = 0.6;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      for (let j = i + 1; j < pts.length; j++) {
        const b = pts[j];
        const dx = a.x - b.x, dy = a.y - b.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < LINK2) {
          const d = Math.sqrt(d2);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(200,255,0,${(0.09 * (1 - d / LINK)).toFixed(3)})`;
          ctx.stroke();
        }
      }
    }
    rafId = requestAnimationFrame(draw);
  }

  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver((entries) => {
      canvasOnScreen = entries.some((entry) => entry.isIntersecting);
      kick();
    });
    io.observe(c);
  }
  document.addEventListener('visibilitychange', kick);
  window.addEventListener('resize', rs);
  rs();
}

// ─── HERO CHART BARS ─────────────────────────────────────────────────────────
export function initChartBars() {
  const c = document.getElementById('chartBars');
  if (!c || c.dataset.init) return;
  c.dataset.init = '1';

  const vals = [30,48,38,62,50,74,58,82,66,90,56,72,84,62,94,70,78,65,88,72,80,68,86,74];
  vals.forEach((v, i) => {
    const b = document.createElement('div');
    b.className = 'bar' + (i === vals.length - 1 ? ' hi' : '');
    b.style.height = '0%';
    c.appendChild(b);
    setTimeout(() => { b.style.height = v + '%'; }, 2600 + i * 50);
  });
}

// ─── LOGO STRIP & MARQUEE ────────────────────────────────────────────────────
export function initLogoStrip() {
  const logoTrack = document.getElementById('logoTrack');
  if (logoTrack && !logoTrack.dataset.anim) {
    logoTrack.dataset.anim = '1';
    gsap.to('#logoTrack', { x: '-50%', duration: 28, ease: 'none', repeat: -1 });
  }
  const mbTrack = document.getElementById('mbTrack');
  if (mbTrack && !mbTrack.dataset.anim) {
    mbTrack.dataset.anim = '1';
    gsap.to('#mbTrack', { x: '-50%', duration: 40, ease: 'none', repeat: -1 });
  }
}

// ─── SCROLL REVEALS ───────────────────────────────────────────────────────────
// Use only y/x transform (no opacity) so content never disappears when triggers are killed or on scroll
export function setupScrollReveal() {
  ScrollTrigger.getAll().forEach(st => st.kill());

  // Reset any inline opacity/transform so content is always visible; only animate position
  const revealSelectors = '.s-eyebrow,.s-title,.s-sub,.eng-card,.prod-card,.why-card,.val-card,.imp-card,.tl-item,.pr-step,.sg-item,.p-tier,.cta-section h2,.cta-section p,.cta-btn,.story-text,.story-visual,.sv-card,.svc-cap,.stack-card,.sp-step,.case-card,.contact-method,.ps-item';
  gsap.utils.toArray(revealSelectors).forEach(el => {
    if (el) { el.style.opacity = ''; el.style.transform = ''; el.style.visibility = ''; }
  });

  gsap.utils.toArray('.s-eyebrow,.s-title,.s-sub').forEach(el => {
    gsap.from(el, { y: 24, duration: .7, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
  });
  gsap.utils.toArray('.eng-card').forEach((c, i) => {
    gsap.from(c, { y: 36, duration: .6, delay: i * .07, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.prod-card,.port-card,.why-card,.val-card,.imp-card,.tl-item').forEach((c, i) => {
    gsap.from(c, { y: 40, duration: .65, delay: (i % 3) * .1, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.pr-step').forEach((c, i) => {
    gsap.from(c, { x: -20, duration: .5, delay: i * .08, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.sg-item').forEach((c, i) => {
    gsap.from(c, { y: 24, duration: .5, delay: i * .07, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.p-tier').forEach((c, i) => {
    gsap.from(c, { y: 36, duration: .6, delay: i * .09, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.contact-method,.ps-item').forEach((c, i) => {
    gsap.from(c, { x: -20, duration: .5, delay: i * .08, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.story-text,.story-visual,.sv-card,.about-hero h1,.about-hero p').forEach(el => {
    gsap.from(el, { y: 24, duration: .7, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
  });
  gsap.utils.toArray('.svc-cap,.stack-card,.sp-step,.case-card').forEach((c, i) => {
    gsap.from(c, { y: 32, duration: .6, delay: (i % 4) * .08, ease: 'power3.out', scrollTrigger: { trigger: c, start: 'top 92%', once: true } });
  });
  gsap.utils.toArray('.svc-hero h1,.svc-hero p,.svc-kpi,.svc-what-text h2,.svc-what-text p').forEach(el => {
    gsap.from(el, { y: 20, duration: .6, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
  });
  gsap.from('.cta-section h2', { y: 36, duration: .8, ease: 'power3.out', scrollTrigger: { trigger: '.cta-section', start: 'top 84%', once: true } });
  gsap.from('.cta-section p',  { y: 20, duration: .6, delay: .1, ease: 'power3.out', scrollTrigger: { trigger: '.cta-section', start: 'top 84%', once: true } });
  gsap.from('.cta-btn',        { y: 20, duration: .6, delay: .2, ease: 'power3.out', scrollTrigger: { trigger: '.cta-section', start: 'top 84%', once: true } });

  ScrollTrigger.refresh();
  setTimeout(() => ScrollTrigger.refresh(), 400);

  // Stat counters
  ScrollTrigger.create({
    trigger: '.stat-grid', start: 'top 86%', once: true,
    onEnter: () => {
      document.querySelectorAll('.sg-val[data-count]').forEach(el => {
        const target = +el.dataset.count;
        const suf = el.dataset.suf || '';
        const plus = target >= 40 ? '+' : '';
        gsap.to({ v: 0 }, {
          v: target, duration: 1.6, ease: 'power2.out',
          onUpdate: function () { el.textContent = Math.round(this.targets()[0].v) + plus + suf; }
        });
      });
    }
  });
}

// ─── CUSTOM CURSOR ────────────────────────────────────────────────────────────
function initCursor() {
  const cur = document.getElementById('cur');
  if (!cur) return;
  let cx = 0, cy = 0, tx = 0, ty = 0;

  document.addEventListener('mousemove', e => {
    tx = e.clientX; ty = e.clientY;
    cur.classList.add('on');
  });
  (function ac() {
    cx += (tx - cx) * .1;
    cy += (ty - cy) * .1;
    cur.style.left = cx + 'px';
    cur.style.top = cy + 'px';
    requestAnimationFrame(ac);
  })();

  document.querySelectorAll('a,button,.eng-card,.p-tier,.prod-card,.port-card,.contact-method,.val-card,.why-card,.tl-item')
    .forEach(el => {
      el.addEventListener('mouseenter', () => cur.classList.add('big'));
      el.addEventListener('mouseleave', () => cur.classList.remove('big'));
    });
}

// ─── NAV HIDE ON SCROLL ───────────────────────────────────────────────────────
function initNavHide() {
  let ly = 0;
  const nv = document.getElementById('nav');
  if (!nv) return;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > ly && y > 120) nv.classList.add('hide');
    else nv.classList.remove('hide');
    ly = y;
  }, { passive: true });
}

// ─── MAGNETIC CTA ─────────────────────────────────────────────────────────────
export function initMagneticCTA() {
  const ctaBtn = document.getElementById('ctaBtn');
  if (!ctaBtn) return;
  ctaBtn.addEventListener('mousemove', e => {
    const r = ctaBtn.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width / 2) * .18;
    const y = (e.clientY - r.top - r.height / 2) * .18;
    ctaBtn.style.transform = `translate(${x}px,${y}px)`;
  });
  ctaBtn.addEventListener('mouseleave', () => { ctaBtn.style.transform = ''; });
}

// ─── PORTFOLIO FILTER ────────────────────────────────────────────────────────
export function filterPort(cat, btn) {
  document.querySelectorAll('.pf-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  document.querySelectorAll('.port-card').forEach(card => {
    if (cat === 'all' || card.dataset.cat.split(' ').indexOf(cat) > -1) {
      gsap.to(card, { opacity: 1, scale: 1, duration: .3, ease: 'power2.out' });
      card.style.display = '';
    } else {
      gsap.to(card, { opacity: .15, scale: .97, duration: .3, ease: 'power2.out' });
    }
  });
}

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
let testiIdx = 0;
let testiTimer = null;

export function testiGo(n) {
  const slides = document.querySelectorAll('.testi-slide');
  const dots = document.querySelectorAll('.tn-dot');
  if (!slides.length) return;
  slides[testiIdx].classList.remove('active');
  dots[testiIdx]?.classList.remove('active');
  testiIdx = (n + slides.length) % slides.length;
  slides[testiIdx].classList.add('active');
  dots[testiIdx]?.classList.add('active');
}

export function testiNav(dir) { testiGo(testiIdx + dir); }

export function startTestiTimer() {
  clearInterval(testiTimer);
  testiTimer = setInterval(() => testiNav(1), 6000);
}

// ─── FAQ ACCORDION ────────────────────────────────────────────────────────────
export function toggleFaq(btn) {
  const item = btn.closest('.faq-item');
  const wasOpen = item.classList.contains('open');
  document.querySelectorAll('.faq-item').forEach(i => i.classList.remove('open'));
  if (!wasOpen) item.classList.add('open');
}

// ─── MAIN INIT ────────────────────────────────────────────────────────────────
function whenLoaderDone(fn) {
  const tick = () => {
    const ld = document.getElementById('loader');
    if (ld && ld.style.display !== 'none') {
      setTimeout(tick, 160);
      return;
    }
    fn();
  };
  tick();
}

export function initV5Shell() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (isIndiaHandoff()) {
    runLoader();
    return;
  }
  runLoader();
  whenLoaderDone(() => {
    setupScrollReveal();
  });

  // Ensure loader is hidden if restored from BFCache
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      const ld = document.getElementById('loader');
      if (ld) ld.style.display = 'none';
    }
  });

  // Mark root ready to fade in
  const root = document.getElementById('root');
  if (root) root.classList.add('ready');
}

